"use client";
import React, { useRef, useEffect } from "react";

const VerificationCodeInput = ({ length = 6, value, onChange, error }) => {
  const inputRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Focus the first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (e, index) => {
    const newValue = e.target.value;

    // Only allow digits
    if (!/^\d*$/.test(newValue)) {
      return;
    }

    // Handle pasting multiple digits
    if (newValue.length > 1) {
      const digits = newValue.split("").slice(0, length);
      const newCode = value.split("");

      digits.forEach((digit, i) => {
        if (index + i < length) {
          newCode[index + i] = digit;
        }
      });

      onChange(newCode.join(""));

      // Focus the appropriate input after pasting
      const nextIndex = Math.min(index + digits.length, length - 1);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
      return;
    }

    // Update the value
    const newCode = value.split("");
    newCode[index] = newValue;
    onChange(newCode.join(""));

    // Auto-focus next input if a digit was entered
    if (newValue && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!value[index] && index > 0) {
        // If current input is empty, focus previous input
        inputRefs.current[index - 1].focus();
      }
    }

    // Handle left arrow key
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1].focus();
    }

    // Handle right arrow key
    if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // Only allow digits
    if (!/^\d*$/.test(pastedData)) {
      return;
    }

    const digits = pastedData.split("").slice(0, length);
    onChange(digits.join("").padEnd(value.length, value.slice(digits.length)));

    // Focus the appropriate input after pasting
    const nextIndex = Math.min(digits.length, length - 1);
    if (inputRefs.current[nextIndex]) {
      inputRefs.current[nextIndex].focus();
    }
  };

  return (
    <div className="w-full">
      <div className="flex justify-between gap-2">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[index] || ""}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            className="w-full h-12 text-center text-xl font-semibold bg-gray-100 text-rose-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-700"
            aria-label={`Digit ${index + 1} of verification code`}
          />
        ))}
      </div>
      {error && <p className="text-xs text-rose-700 mt-1">{error}</p>}
    </div>
  );
};

export default VerificationCodeInput;
